import React, { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, User, Minus, Plus, Trash2, Lock, Check, AlertCircle, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
// Square Production credentials
const SQUARE_APP_ID = 'sq0idp-kjj929wOAzlqekWE679Xvg';
const SQUARE_LOCATION_ID = 'LPDVKC3YERSN5';

const sendPostMessage = (data) => {
  if (window.parent !== window) {
    window.parent.postMessage(data, 'https://bioinfinitylab.com');
    window.parent.postMessage(data, 'https://www.bioinfinitylab.com');
    window.parent.postMessage(data, 'https://bioinfinity.co');
    window.parent.postMessage(data, 'https://www.bioinfinity.co');
  }
};

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_44e46f47-7fd3-499d-a564-02f2c012966a/artifacts/530zfrkj_aceofmiamilogo.png";

// Bentley Bentayga W12 Speed - Real Photos
const BENTLEY_PHOTOS = {
  front: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/rq3wz6x5_DSCF0607.jpg",
  side: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/pj0r4xiw_DSCF0610.jpg",
  angle: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/e9wnqyuc_DSCF0622.jpg",
  rear: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/fkgi5lny_DSCF0635.JPG",
  beauty: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/6x8m1082_DSCF0674.JPG"
};

// Mercedes-Benz Maybach GLS 600 - Real Photos
const MAYBACH_PHOTOS = {
  rear: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/maf1zel7_DSCF0585.jpg",
  back: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/lqdeul0d_DSCF0589.jpg",
  front: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/23z4h8wd_DSCF0595.jpg",
  side: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/b1tkt1yc_DSCF0597.jpg",
  interior: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/7dkjnfvp_DSCF0600.jpg"
};

// Mercedes-Benz SL63 AMG - Real Photos
const SL63_PHOTOS = {
  rear1: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/yngp6jrt_75E71353-6522-41E0-A40A-C39CC6CD6A18.jpg",
  rear2: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/p9ut3hgs_F361D990-3FA1-426C-A7FE-2E28CE82EA74.jpg"
};

// Stock images for other vehicles
const STOCK_IMAGES = {
  bmwM440: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/g6bmcert_bmw-m4-competition-2024-cabrio-one.jpg",
  g63: "https://images.unsplash.com/photo-1767373926801-833d96b26c73?w=800&q=80",
  teslaY: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/nirrro9k_newmodely-68-6793b87cd572c.jpg",
  jeepWrangler: "https://customer-assets.emergentagent.com/job_ace-stripe/artifacts/t7zl4c7a_jp024_262wr.webp"
};

// Car images
const CARS = {
  bentley: BENTLEY_PHOTOS.front,
  maybach: MAYBACH_PHOTOS.front,
  amg: SL63_PHOTOS.rear1,
  hero: BENTLEY_PHOTOS.angle
};

// Fleet data
const FLEET_DATA = [
  {
    id: 'bentley-bentayga',
    name: 'BENTLEY BENTAYGA',
    model: 'W12 SPEED',
    price: 559,
    images: BENTLEY_PHOTOS,
    mainImage: BENTLEY_PHOTOS.front,
    specs: { topSpeed: '190 MPH', acceleration: '3.8s', power: '626 BHP', torque: '900 NM' },
    description: 'The Bentley Bentayga W12 Speed is the world\'s fastest SUV, combining extraordinary performance with unmatched luxury.',
    features: ['W12 Twin-Turbo Engine', 'All-Wheel Drive', 'Air Suspension', 'Mulliner Specification', 'Naim Audio System', 'Panoramic Roof']
  },
  {
    id: 'maybach-gls600',
    name: 'MERCEDES-BENZ',
    model: 'MAYBACH GLS 600',
    price: 449,
    images: MAYBACH_PHOTOS,
    mainImage: MAYBACH_PHOTOS.front,
    specs: { topSpeed: '174 MPH', acceleration: '4.8s', power: '550 BHP', torque: '730 NM' },
    description: 'The Mercedes-Maybach GLS 600 represents the ultimate in luxury SUVs with opulent interior and first-class travel.',
    features: ['4.0L V8 Biturbo + EQ Boost', 'Executive Rear Seats', 'Burmester 3D Audio', 'MBUX Entertainment', 'Refrigerator Compartment', 'Champagne Flutes']
  },
  {
    id: 'g63-amg',
    name: 'MERCEDES-BENZ',
    model: 'G63 AMG',
    price: 449,
    images: { main: STOCK_IMAGES.g63 },
    mainImage: STOCK_IMAGES.g63,
    specs: { topSpeed: '149 MPH', acceleration: '4.5s', power: '577 BHP', torque: '850 NM' },
    description: 'The Mercedes-AMG G63 is an icon of luxury and power. Handcrafted AMG engine meets legendary G-Class capability.',
    features: ['4.0L V8 Biturbo', 'AMG Performance 4MATIC', 'AMG Ride Control', 'Burmester Audio', 'Luxury Interior', 'Off-Road Capability']
  },
  {
    id: 'sl63-amg',
    name: 'MERCEDES-BENZ',
    model: 'SL63 AMG',
    price: 329,
    images: SL63_PHOTOS,
    mainImage: SL63_PHOTOS.rear1,
    specs: { topSpeed: '196 MPH', acceleration: '3.5s', power: '577 BHP', torque: '800 NM' },
    description: 'The Mercedes-AMG SL63 is a legendary roadster reborn. Pure driving exhilaration with stunning design.',
    features: ['4.0L V8 Biturbo', 'AMG Performance 4MATIC+', 'Active Aerodynamics', 'AMG Ride Control', 'Retractable Soft Top', 'AMG Track Pace']
  },
  {
    id: 'bmw-m440',
    name: 'BMW',
    model: 'M440i CONVERTIBLE',
    price: 229,
    images: { main: STOCK_IMAGES.bmwM440 },
    mainImage: STOCK_IMAGES.bmwM440,
    specs: { topSpeed: '155 MPH', acceleration: '4.4s', power: '382 BHP', torque: '500 NM' },
    description: 'The 2024 BMW M440i Convertible delivers thrilling performance with open-air freedom and premium luxury.',
    features: ['3.0L TwinPower Turbo I6', 'xDrive AWD', 'Soft-Top Convertible', 'M Sport Package', 'Live Cockpit Pro', 'Harman Kardon Audio']
  },
  {
    id: 'tesla-model-y',
    name: 'TESLA',
    model: 'MODEL Y JUNIPER',
    price: 99,
    images: { main: STOCK_IMAGES.teslaY },
    mainImage: STOCK_IMAGES.teslaY,
    specs: { topSpeed: '135 MPH', acceleration: '4.8s', power: '384 BHP', range: '320 mi' },
    description: 'The 2025 Tesla Model Y Juniper offers cutting-edge electric performance with updated design and extended range.',
    features: ['Dual Motor AWD', 'Autopilot', '15" Touchscreen', 'Premium Audio', 'Glass Roof', 'Supercharging Network']
  },
  {
    id: 'jeep-wrangler',
    name: 'JEEP',
    model: 'WRANGLER RUBICON',
    price: 169,
    images: { main: STOCK_IMAGES.jeepWrangler },
    mainImage: STOCK_IMAGES.jeepWrangler,
    specs: { topSpeed: '100 MPH', acceleration: '7.5s', power: '285 BHP', torque: '260 lb-ft' },
    description: 'The 2025 Jeep Wrangler Rubicon is the ultimate off-road machine. Conquer any terrain in iconic style.',
    features: ['3.6L Pentastar V6', 'Rock-Trac 4x4', 'Electronic Locking Diffs', 'Removable Top & Doors', 'Skid Plates', 'Trail Rated']
  }
];

// ============== MERCH SHOP DATA ==============

// Product Images from user uploads
const MERCH_IMAGES = {
  trackJacket: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/4j02dq03_%E2%80%9CAOM%20Track%20Jacket%E2%80%9D%20Product_%20Black%20luxury%20varsity-style%20jacket%20Matte%20fabric%20with%20.png",
  carbonCrew: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/pv9daxr6_%E2%80%9CCarbon%20Edition%20Crew%E2%80%9D%20Dark%20grey%20Thick%20fabric%20Front_%20Large%20centered%20logo%20Lightnin.png",
  coastalCrew: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/azat0zvh_%E2%80%9CCoastal%20Cream%20Crew%E2%80%9D%20Sand%20beige%20sweater%20Slight%20oversized%20Front_%20Small%20gold%20embro.png",
  executiveCrew: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/2a4ieiux_%E2%80%9CExecutive%20Black%20Crew%E2%80%9D%20Jet%20black%20premium%20crewneck%20Slim%20luxury%20fit%20Front_%20Tonal%20e.png",
  miamiGoldHoodie: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/qa41iohi_%E2%80%9CMiami%20Heat%20Gold%E2%80%9D%20Hoodie%20Product_%20Deep%20charcoal%20hoodie%20Slight%20boxy%20luxury%20fit%20Fr.png",
  trackEditionHoodie: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/q17s8e3z_%E2%80%9CTrack%20Edition%E2%80%9D%20Hoodie%20Product_%20Cream%20_%20Off-white%20hoodie%20Premium%20soft%20cotton%20Fro.png",
  blackLuxuryCap: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/pie31ngb_Black%20Luxury%20Cap%20Structured%20snapback%20Matte%20black%20Front_%20Embroidered%20gold%20lightni.png",
  creamSummerCap: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/rzphrzuv_Cream%20Summer%20Cap%20Soft%20unstructured%20Beige%20_%20sand%20color%20Front_%20Small%20embroidered%20A.png",
  midnightHoodie: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/zg1k89tb_Create%20a%20premium%20luxury%20streetwear%20product%20image.%20Product_%20ACE%20OF%20MIAMI%20%E2%80%93%20Midnig.png",
  miamiWhiteTee: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/rumoz8ph_Miami%20White%20Tee%20Crisp%20white%20Front_%20Centered%20logo%20Back_%20Small%20text_%20%E2%80%9CTHE%20ACE%20OF%20M.png",
  minimalBlackTee: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/n8gbehnf_Minimal%20Black%20Tee%20Heavy%20cotton%20Oversized%20Front_%20Small%20chest%20logo%20Back_%20Huge%20vert.png",
  performanceGreyTee: "https://customer-assets.emergentagent.com/job_members-only-ace/artifacts/1txs83nr_Performance%20Grey%20Tee%20Athletic%20grey%20Front_%20Lightning%20bolt%20only%20Small%20under%20it_%20AO.png",
};

const MERCH_PRODUCTS = [
  {
    id: "aom-track-jacket",
    name: "AOM Track Jacket",
    category: "jackets",
    price: 285,
    description: "Black luxury varsity-style jacket with matte fabric finish. Our signature piece featuring premium construction and timeless design.",
    fabric: "100% Premium Nylon • Matte finish • YKK zippers • Ribbed cuffs",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.trackJacket
  },
  {
    id: "carbon-edition-crew",
    name: "Carbon Edition Crew",
    category: "sweaters",
    price: 165,
    description: "Dark grey thick fabric crew with large centered logo and lightning-inspired detailing. Bold statement piece.",
    fabric: "85% Cotton, 15% Polyester • 400 GSM • Ribbed trim",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.carbonCrew
  },
  {
    id: "coastal-cream-crew",
    name: "Coastal Cream Crew",
    category: "sweaters",
    price: 155,
    description: "Sand beige sweater with slight oversized fit. Small gold embroidered logo for understated luxury.",
    fabric: "70% Cotton, 30% Cashmere blend • Relaxed fit • Gold thread detail",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.coastalCrew
  },
  {
    id: "executive-black-crew",
    name: "Executive Black Crew",
    category: "sweaters",
    price: 145,
    description: "Jet black premium crewneck with slim luxury fit. Tonal embroidered logo for refined elegance.",
    fabric: "100% Premium Cotton • Slim fit • Tonal branding",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.executiveCrew
  },
  {
    id: "miami-heat-gold-hoodie",
    name: "Miami Heat Gold Hoodie",
    category: "hoodies",
    price: 195,
    description: "Deep charcoal hoodie with slight boxy luxury fit. Gold accented branding embodies Miami's vibrant energy.",
    fabric: "80% Cotton, 20% Polyester • 450 GSM • Gold embroidery",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.miamiGoldHoodie
  },
  {
    id: "track-edition-hoodie",
    name: "Track Edition Hoodie",
    category: "hoodies",
    price: 185,
    description: "Cream off-white hoodie in premium soft cotton. Clean minimal design with subtle ACE branding.",
    fabric: "100% Premium Cotton • 420 GSM • Soft brushed interior",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.trackEditionHoodie
  },
  {
    id: "midnight-performance-hoodie",
    name: "Midnight Performance Hoodie",
    category: "hoodies",
    price: 175,
    description: "Premium heavyweight hoodie crafted for ultimate comfort. Features our signature minimal branding and athletic-inspired design.",
    fabric: "80% Cotton, 20% Polyester • 400 GSM • Brushed interior",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.midnightHoodie
  },
  {
    id: "miami-white-tee",
    name: "Miami White Tee",
    category: "tshirts",
    price: 85,
    description: "Crisp white tee with centered logo on front. Back features 'THE ACE OF MIAMI' text. Essential wardrobe piece.",
    fabric: "100% Pima Cotton • 180 GSM • Preshrunk • Relaxed fit",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.miamiWhiteTee
  },
  {
    id: "black-luxury-cap",
    name: "Black Luxury Cap",
    category: "caps",
    price: 65,
    description: "Structured snapback in matte black. Embroidered gold lightning bolt logo on front. Premium hardware.",
    fabric: "100% Cotton twill • Gold embroidery • Snapback closure",
    sizes: ["One Size"],
    image: MERCH_IMAGES.blackLuxuryCap
  },
  {
    id: "cream-summer-cap",
    name: "Cream Summer Cap",
    category: "caps",
    price: 55,
    description: "Soft unstructured cap in beige/sand color. Small embroidered ACE logo for understated style.",
    fabric: "100% Washed cotton • Leather strap closure • Tonal logo",
    sizes: ["One Size"],
    image: MERCH_IMAGES.creamSummerCap
  },
  {
    id: "minimal-black-tee",
    name: "Minimal Black Tee",
    category: "tshirts",
    price: 90,
    description: "Heavy cotton oversized tee with small chest logo. Back features huge vertical ACE branding. Statement piece.",
    fabric: "100% Heavy Cotton • 220 GSM • Oversized fit • Screenprint back",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.minimalBlackTee
  },
  {
    id: "performance-grey-tee",
    name: "Performance Grey Tee",
    category: "tshirts",
    price: 80,
    description: "Athletic grey tee with lightning bolt design and small AOM text. Engineered for comfort and style.",
    fabric: "60% Cotton, 40% Modal • Quick-dry technology • Athletic fit",
    sizes: ["S", "M", "L", "XL", "XXL"],
    image: MERCH_IMAGES.performanceGreyTee
  }
];

const MERCH_CATEGORIES = [
  { id: "all", name: "All" },
  { id: "hoodies", name: "Hoodies" },
  { id: "sweaters", name: "Sweaters" },
  { id: "jackets", name: "Jackets" },
  { id: "tshirts", name: "T-Shirts" },
  { id: "caps", name: "Caps" }
];

// ============== AUTH CONTEXT FOR SHOP ==============

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);

  const fetchCart = async (token) => {
    try {
      const { data } = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(data.items || []);
    } catch (e) {
      console.error("Error fetching cart:", e);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("aom_token");
      if (token) {
        try {
          const { data } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(data.user);
          fetchCart(token);
        } catch (e) {
          localStorage.removeItem("aom_token");
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const fetchUser = async (token) => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data.user);
      fetchCart(token);
    } catch (e) {
      localStorage.removeItem("aom_token");
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("aom_token", data.token);
    setUser(data.user);
    fetchCart(data.token);
    return data;
  };

  const register = async (email, password, full_name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, full_name });
    localStorage.setItem("aom_token", data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("aom_token");
    setUser(null);
    setCart([]);
  };

  const addToCart = async (product_id, size, quantity = 1) => {
    const token = localStorage.getItem("aom_token");
    if (!token) throw new Error("Please login first");
    await axios.post(`${API}/cart/add`, { product_id, size, quantity }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchCart(token);
  };

  const removeFromCart = async (item_id) => {
    const token = localStorage.getItem("aom_token");
    await axios.delete(`${API}/cart/${item_id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchCart(token);
  };

  const updateCartItem = async (item_id, quantity, size) => {
    const token = localStorage.getItem("aom_token");
    await axios.put(`${API}/cart/${item_id}`, { product_id: "", size, quantity }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchCart(token);
  };

  const refreshCart = () => {
    const token = localStorage.getItem("aom_token");
    if (token) fetchCart(token);
  };

  return (
    <AuthContext.Provider value={{ user, loading, cart, login, register, logout, addToCart, removeFromCart, updateCartItem, refreshCart, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Header Component
const Header = () => {
  const auth = useAuth();
  const cartCount = auth?.cart?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
  <header className="header">
    <div className="header-content">
      <Link to="/" className="logo">
        <img src={LOGO_URL} alt="Ace of Miami" />
      </Link>
      
      {/* Desktop Navigation */}
      <nav className="nav desktop-nav">
        <Link to="/" className="nav-link">HOME</Link>
        <Link to="/shop" className="nav-link">SHOP</Link>
        <Link to="/about" className="nav-link">ABOUT</Link>
        <Link to="/contact" className="nav-link">CONTACT</Link>
      </nav>
      
      {/* Desktop Actions */}
      <div className="header-actions desktop-actions">
        {auth?.user ? (
          <Link to="/account" className="nav-link user-link">
            <User size={18} />
          </Link>
        ) : (
          <Link to="/login" className="nav-link">LOGIN</Link>
        )}
        <Link to="/cart" className="cart-link">
          <ShoppingBag size={18} />
          {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
        </Link>
        <Link to="/shop" className="btn-yellow">
          SHOP NOW
        </Link>
      </div>
      
      {/* Mobile Menu Toggle */}
      <button 
        className="mobile-menu-toggle" 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      
      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav className="mobile-nav">
          <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>HOME</Link>
          <Link to="/shop" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>SHOP</Link>
          <Link to="/about" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>ABOUT</Link>
          <Link to="/contact" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>CONTACT</Link>
          <div className="mobile-nav-divider"></div>
          <Link to="/cart" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            CART {cartCount > 0 && `(${cartCount})`}
          </Link>
          {auth?.user ? (
            <Link to="/account" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>MY ACCOUNT</Link>
          ) : (
            <Link to="/login" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>LOGIN</Link>
          )}
        </nav>
      )}
    </div>
  </header>
);
};

// Footer Component
const Footer = () => (
  <footer className="footer">
    <div className="footer-content">
      <p>Ace of Miami © 2026. All Rights Reserved</p>
      <div className="footer-links">
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/terms">Terms Of Use</Link>
      </div>
    </div>
  </footer>
);

// Landing Page - Clothing Shop
const LandingPage = () => {
  const featuredProducts = MERCH_PRODUCTS.slice(0, 4);
  const categories = [
    { name: "HOODIES", slug: "hoodies", count: MERCH_PRODUCTS.filter(p => p.category === "hoodies").length },
    { name: "SWEATERS", slug: "sweaters", count: MERCH_PRODUCTS.filter(p => p.category === "sweaters").length },
    { name: "T-SHIRTS", slug: "tshirts", count: MERCH_PRODUCTS.filter(p => p.category === "tshirts").length },
    { name: "JACKETS", slug: "jackets", count: MERCH_PRODUCTS.filter(p => p.category === "jackets").length },
    { name: "CAPS", slug: "caps", count: MERCH_PRODUCTS.filter(p => p.category === "caps").length },
  ];

  return (
    <div className="site" data-testid="landing-page">
      <Header />

      {/* Hero Section */}
      <section className="hero clothing-hero">
        <div className="hero-bg">
          <img src={MERCH_IMAGES.trackJacket} alt="ACE OF MIAMI Collection" className="hero-bg-img" />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <span className="hero-tag">NEW COLLECTION</span>
            <h1 className="hero-title">
              PREMIUM<br/>
              <span className="gold">STREETWEAR</span><br/>
              MIAMI
            </h1>
            <p className="hero-subtitle">Luxury clothing for those who demand the best</p>
            <Link to="/shop" className="hero-cta">
              SHOP NOW <span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="section-header">
          <h2>SHOP BY CATEGORY</h2>
        </div>
        <div className="categories-grid">
          {categories.map((cat) => (
            <Link to={`/shop?category=${cat.slug}`} key={cat.slug} className="category-card">
              <span className="category-name">{cat.name}</span>
              <span className="category-count">{cat.count} items</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="featured-section">
        <div className="section-header">
          <h2>FEATURED PIECES</h2>
          <Link to="/shop" className="view-all">VIEW ALL →</Link>
        </div>
        <div className="products-grid">
          {featuredProducts.map((product) => (
            <Link to={`/shop/${product.id}`} key={product.id} className="product-card">
              <div className="product-image">
                <img src={product.image} alt={product.name} />
              </div>
              <div className="product-info">
                <h4>{product.name}</h4>
                <p className="product-price">${product.price}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="about">
        <div className="about-content">
          <div className="about-left">
            <h2>ABOUT ACE OF<br/>MIAMI</h2>
          </div>
          <div className="about-right">
            <p>Ace of Miami brings you premium streetwear with a luxury edge. Our collections combine Miami's vibrant energy with exceptional quality materials and craftsmanship. Each piece is designed for those who appreciate the finer things in life.</p>
            <Link to="/about" className="btn-outline">LEARN MORE</Link>
          </div>
        </div>
      </section>

      {/* Best Sellers Section */}
      <section className="bestsellers-section">
        <div className="section-header">
          <h2>BEST SELLERS</h2>
        </div>
        <div className="products-grid">
          {MERCH_PRODUCTS.slice(4, 8).map((product) => (
            <Link to={`/shop/${product.id}`} key={product.id} className="product-card">
              <div className="product-image">
                <img src={product.image} alt={product.name} />
              </div>
              <div className="product-info">
                <h4>{product.name}</h4>
                <p className="product-price">${product.price}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why Section */}
      <section className="why">
        <div className="why-content">
          <div className="why-left">
            <h2>WHY CHOOSE<br/>ACE OF MIAMI</h2>
            <p>We're committed to delivering premium quality streetwear that stands out. Every piece in our collection is crafted with attention to detail, using only the finest materials. From our signature hoodies to our exclusive caps, each item represents the pinnacle of Miami style.</p>
            <div className="brand-logos">
              <div className="brand">PREMIUM</div>
              <div className="brand">QUALITY</div>
              <div className="brand">EXCLUSIVE</div>
              <div className="brand">MIAMI</div>
            </div>
          </div>
          <div className="why-right">
            <img src={MERCH_IMAGES.miamiGoldHoodie} alt="Premium Streetwear" />
          </div>
        </div>
      </section>

      {/* Newsletter/CTA Section */}
      <section className="cta">
        <h2>JOIN THE<br/>ACE CLUB</h2>
        <p>Get exclusive access to new drops, members-only pricing, and VIP perks.</p>
        <div className="cta-buttons">
          <Link to="/membership" className="btn-yellow">BECOME A MEMBER</Link>
          <Link to="/shop" className="btn-outline">SHOP NOW</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div className="site" data-testid="about-page">
      <Header />
      
      {/* About Hero */}
      <section className="page-hero">
        <div className="page-hero-bg">
          <img src={MERCH_IMAGES.trackJacket} alt="ACE OF MIAMI" className="page-hero-img" />
          <div className="page-hero-overlay"></div>
        </div>
        <div className="page-hero-content">
          <span className="page-tag">ABOUT US</span>
          <h1>THE ACE OF<br/>MIAMI</h1>
          <p>Premium Streetwear. Miami Style.</p>
        </div>
      </section>

      {/* About Content */}
      <section className="about-section">
        <div className="about-grid">
          <div className="about-text">
            <h2>OUR STORY</h2>
            <p>Ace of Miami was born from a passion for premium streetwear and Miami's vibrant culture. What started as an exclusive collection for our inner circle has grown into one of Miami's most sought-after clothing brands.</p>
            <p>Every piece in our collection is designed with intention and crafted with the finest materials. We believe in quality over quantity, exclusivity over mass production, and style that makes a statement.</p>
          </div>
          <div className="about-image">
            <img src={MERCH_IMAGES.miamiGoldHoodie} alt="ACE OF MIAMI Hoodie" />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <h2>THE ACE DIFFERENCE</h2>
        <div className="values-grid">
          <div className="value-card">
            <div className="value-icon">★</div>
            <h3>PREMIUM MATERIALS</h3>
            <p>Only the finest fabrics from premium cotton to cashmere blends.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">◆</div>
            <h3>LIMITED DROPS</h3>
            <p>Exclusive releases ensure you're wearing something truly unique.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">●</div>
            <h3>MIAMI DESIGNED</h3>
            <p>Every piece captures the energy and style of Miami.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">■</div>
            <h3>MEMBERS FIRST</h3>
            <p>Our members get early access, exclusive pricing, and VIP perks.</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          <div className="stat">
            <span className="stat-number">1000+</span>
            <span className="stat-label">Happy Customers</span>
          </div>
          <div className="stat">
            <span className="stat-number">12</span>
            <span className="stat-label">Exclusive Pieces</span>
          </div>
          <div className="stat">
            <span className="stat-number">5★</span>
            <span className="stat-label">Average Rating</span>
          </div>
          <div className="stat">
            <span className="stat-number">MIAMI</span>
            <span className="stat-label">Based</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <h2>JOIN THE<br/>ACE MOVEMENT</h2>
        <p>Become a member and get exclusive access.</p>
        <div className="cta-buttons">
          <Link to="/membership" className="btn-yellow">BECOME A MEMBER</Link>
          <Link to="/shop" className="btn-outline">SHOP NOW</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Fleet Page
const FleetPage = () => {
  return (
    <div className="site" data-testid="fleet-page">
      <Header />
      
      {/* Fleet Hero */}
      <section className="page-hero small">
        <div className="page-hero-bg">
          <img src={SL63_PHOTOS.rear2} alt="Luxury Fleet" className="page-hero-img" />
          <div className="page-hero-overlay"></div>
        </div>
        <div className="page-hero-content">
          <span className="page-tag">OUR COLLECTION</span>
          <h1>THE FLEET</h1>
          <p>Discover Miami's finest luxury vehicles</p>
        </div>
      </section>

      {/* Fleet Grid */}
      <section className="fleet-section">
        <div className="fleet-intro">
          <h2>AVAILABLE VEHICLES</h2>
          <p>Each vehicle in our fleet is meticulously maintained and detailed to ensure the ultimate driving experience. All rentals include comprehensive insurance and 24/7 roadside assistance.</p>
        </div>
        
        <div className="fleet-full-grid">
          {FLEET_DATA.map((car) => (
            <div key={car.id} className="fleet-card">
              <div className="fleet-card-image">
                <img src={car.mainImage} alt={`${car.name} ${car.model}`} />
                <div className="fleet-card-overlay">
                  <Link to="/contact" className="btn-yellow small">BOOK NOW</Link>
                </div>
              </div>
              <div className="fleet-card-content">
                <h3>{car.name}<br/><span>{car.model}</span></h3>
                <p className="fleet-card-desc">{car.description}</p>
                <div className="fleet-card-specs">
                  <div className="fleet-spec">
                    <strong>{car.specs.topSpeed}</strong>
                    <span>Top Speed</span>
                  </div>
                  <div className="fleet-spec">
                    <strong>{car.specs.acceleration}</strong>
                    <span>0-60mph</span>
                  </div>
                  <div className="fleet-spec">
                    <strong>{car.specs.power}</strong>
                    <span>Power</span>
                  </div>
                </div>
                <div className="fleet-card-features">
                  {car.features.slice(0, 3).map((feature, i) => (
                    <span key={i} className="feature-tag">{feature}</span>
                  ))}
                </div>
                <div className="fleet-card-footer">
                  <div className="fleet-card-price">
                    <span className="from">From</span>
                    <span className="amount">${car.price}</span>
                    <span className="unit">/day</span>
                  </div>
                  <Link to="/contact" className="btn-yellow">RESERVE</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Booking Info */}
      <section className="booking-info">
        <div className="booking-content">
          <h2>HOW TO BOOK</h2>
          <div className="booking-steps">
            <div className="booking-step">
              <span className="step-number">01</span>
              <h4>CHOOSE YOUR CAR</h4>
              <p>Browse our fleet and select the perfect vehicle for your needs.</p>
            </div>
            <div className="booking-step">
              <span className="step-number">02</span>
              <h4>BOOK NOW</h4>
              <p>Complete your reservation through our Turo listings.</p>
            </div>
            <div className="booking-step">
              <span className="step-number">03</span>
              <h4>WE DELIVER</h4>
              <p>We'll bring the car to your location in Miami.</p>
            </div>
            <div className="booking-step">
              <span className="step-number">04</span>
              <h4>ENJOY THE RIDE</h4>
              <p>Experience luxury driving at its finest.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <h2>READY TO<br/>GET STARTED?</h2>
        <p>Book your dream car today on Turo.</p>
        <div className="cta-buttons">
          <Link to="/contact" className="btn-yellow">BOOK NOW</Link>
          <Link to="/contact" className="btn-outline">CONTACT US</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  useEffect(() => { sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'success', orderId: searchParams.get("orderId"), sessionId: searchParams.get("session_id") }); }, [searchParams]);
  return <div className="checkout-page"><div className="checkout-card success"><div className="result-icon">✓</div><h2>Payment Successful!</h2></div></div>;
};

const CheckoutCancel = () => {
  const [searchParams] = useSearchParams();
  useEffect(() => { sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'cancelled', orderId: searchParams.get("orderId") }); }, [searchParams]);
  return <div className="checkout-page"><div className="checkout-card"><div className="result-icon cancel">✕</div><h2>Payment Cancelled</h2></div></div>;
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send to backend
    setSubmitted(true);
  };

  return (
    <div className="site" data-testid="contact-page">
      <Header />
      
      <section className="page-hero small">
        <div className="page-hero-bg">
          <img src={MERCH_IMAGES.trackJacket} alt="Contact Us" className="page-hero-img" />
          <div className="page-hero-overlay"></div>
        </div>
        <div className="page-hero-content">
          <span className="page-tag">GET IN TOUCH</span>
          <h1>CONTACT US</h1>
          <p>Questions about orders, sizing, or membership?</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="contact-grid">
          <div className="contact-info">
            <h2>REACH OUT</h2>
            <p>Have questions about our clothing, your order, or membership? Fill out the form and our team will get back to you within 24 hours.</p>
            
            <div className="contact-details">
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <div>
                  <strong>Location</strong>
                  <p>Miami, Florida</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <div>
                  <strong>Email</strong>
                  <p>info@aceofmiami.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-wrapper">
            {submitted ? (
              <div className="form-success">
                <div className="success-icon-large">✓</div>
                <h3>Thank You!</h3>
                <p>We've received your message and will contact you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Your name" />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 555-5555" />
                  </div>
                  <div className="form-group">
                    <label>Topic</label>
                    <select value={formData.interest} onChange={(e) => setFormData({...formData, interest: e.target.value})}>
                      <option value="">Select a topic</option>
                      <option value="order">Order Inquiry</option>
                      <option value="sizing">Sizing Question</option>
                      <option value="membership">Membership</option>
                      <option value="returns">Returns & Exchanges</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-group full">
                  <label>Message</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us how we can help..." rows="4"></textarea>
                </div>
                <button type="submit" className="btn-yellow large">SEND MESSAGE</button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <div className="site" data-testid="privacy-page">
      <Header />
      
      <section className="legal-hero">
        <h1>PRIVACY POLICY</h1>
        <p>Last Updated: March 2026</p>
      </section>

      <section className="legal-content">
        <div className="legal-container">
          <h2>1. Introduction</h2>
          <p>Ace of Miami ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website aceofmiami.com or purchase our clothing and merchandise.</p>

          <h2>2. Information We Collect</h2>
          <h3>Personal Information</h3>
          <p>We may collect personal information that you voluntarily provide to us when you:</p>
          <ul>
            <li>Make a purchase on our website</li>
            <li>Create an account or apply for membership</li>
            <li>Contact us via email or contact forms</li>
            <li>Subscribe to our newsletter</li>
          </ul>
          <p>This information may include:</p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Shipping address</li>
            <li>Billing and payment information</li>
            <li>Order history and preferences</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <p>When you visit our website, we may automatically collect certain information, including:</p>
          <ul>
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Pages visited and time spent</li>
            <li>Referring website</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Process and fulfill your orders</li>
            <li>Send order confirmations and shipping updates</li>
            <li>Process payments and prevent fraud</li>
            <li>Communicate with you about your purchases</li>
            <li>Send promotional offers and updates (with your consent)</li>
            <li>Improve our website and services</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. Information Sharing</h2>
          <p>We may share your information with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Third parties who assist us in operating our business (payment processors, shipping carriers)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>

          <h2>5. Data Security</h2>
          <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.</p>

          <h2>6. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent where applicable</li>
          </ul>

          <h2>7. Cookies</h2>
          <p>Our website uses cookies and similar tracking technologies to enhance your browsing experience. You can control cookie settings through your browser preferences.</p>

          <h2>8. Third-Party Links</h2>
          <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites.</p>

          <h2>9. Children's Privacy</h2>
          <p>Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from minors.</p>

          <h2>10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date.</p>

          <h2>11. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, please contact us at:</p>
          <p><strong>Ace of Miami</strong><br/>
          Miami, Florida<br/>
          Email: info@aceofmiami.com</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Terms of Use Page
const TermsOfUsePage = () => {
  return (
    <div className="site" data-testid="terms-page">
      <Header />
      
      <section className="legal-hero">
        <h1>TERMS OF USE</h1>
        <p>Last Updated: March 2026</p>
      </section>

      <section className="legal-content">
        <div className="legal-container">
          <h2>1. Agreement to Terms</h2>
          <p>By accessing or using the Ace of Miami website (aceofmiami.com) and services, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.</p>

          <h2>2. Account Requirements</h2>
          <h3>Age Requirements</h3>
          <ul>
            <li>Users must be at least 18 years of age to make purchases</li>
            <li>Members must provide accurate personal information</li>
          </ul>

          <h3>Account Information</h3>
          <ul>
            <li>You are responsible for maintaining the confidentiality of your account</li>
            <li>You must provide accurate and complete information</li>
            <li>You are responsible for all activities under your account</li>
          </ul>

          <h2>3. Orders and Payment</h2>
          <ul>
            <li>All orders require a valid payment method</li>
            <li>Prices are in USD and subject to change without notice</li>
            <li>We accept Visa, Mastercard, American Express, and other major payment methods</li>
            <li>Orders are subject to availability</li>
            <li>We reserve the right to refuse or cancel orders</li>
          </ul>

          <h2>4. Shipping Policy</h2>
          <ul>
            <li>Orders are processed within 1-3 business days</li>
            <li>Shipping times vary by location and method selected</li>
            <li>International orders may be subject to customs duties</li>
            <li>We are not responsible for delays caused by carriers</li>
          </ul>

          <h2>5. Returns and Exchanges</h2>
          <ul>
            <li><strong>Within 14 days:</strong> Full refund for unworn items with tags attached</li>
            <li><strong>14-30 days:</strong> Store credit or exchange only</li>
            <li><strong>After 30 days:</strong> No returns accepted</li>
            <li>Sale items are final sale</li>
          </ul>

          <h2>6. Product Information</h2>
          <ul>
            <li>We strive to display products accurately</li>
            <li>Colors may vary slightly due to monitor settings</li>
            <li>Product descriptions are for informational purposes</li>
            <li>We reserve the right to correct errors in pricing or descriptions</li>
          </ul>

          <h2>7. Membership Program</h2>
          <ul>
            <li>Membership is by application only</li>
            <li>Members receive exclusive pricing and early access to drops</li>
            <li>Membership benefits are non-transferable</li>
            <li>We reserve the right to revoke membership at our discretion</li>
          </ul>

          <h2>8. Intellectual Property</h2>
          <ul>
            <li>All content on this site is owned by Ace of Miami</li>
            <li>You may not reproduce, distribute, or use our content without permission</li>
            <li>Our trademarks and logos are protected</li>
          </ul>

          <h2>9. Limitation of Liability</h2>
          <p>Ace of Miami shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products. Our total liability shall not exceed the amount paid for your order.</p>

          <h2>10. Indemnification</h2>
          <p>You agree to indemnify and hold harmless Ace of Miami, its owners, employees, and agents from any claims, damages, losses, or expenses arising from your use of our services or violation of these terms.</p>

          <h2>11. Governing Law</h2>
          <p>These Terms of Use shall be governed by and construed in accordance with the laws of the State of Florida. Any disputes shall be resolved in the courts of Miami-Dade County, Florida.</p>

          <h2>12. Changes to Terms</h2>
          <p>We reserve the right to modify these Terms of Use at any time. Continued use of our services after changes constitutes acceptance of the modified terms.</p>

          <h2>13. Contact Information</h2>
          <p>For questions regarding these Terms of Use, please contact:</p>
          <p><strong>Ace of Miami</strong><br/>
          Miami, Florida<br/>
          Email: info@aceofmiami.com</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// ============== SHOP PAGES ==============

// Product Card Component
const MerchProductCard = ({ product }) => {
  const navigate = useNavigate();
  
  return (
    <div className="merch-product-card" onClick={() => navigate(`/shop/${product.id}`)} data-testid={`product-${product.id}`}>
      <div className="merch-product-image">
        <img src={product.image} alt={product.name} />
      </div>
      <div className="merch-product-info">
        <h3>{product.name}</h3>
        <p className="merch-product-price">${product.price}</p>
      </div>
    </div>
  );
};

// Shop Page
const ShopPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  
  const filteredProducts = activeCategory === "all" 
    ? MERCH_PRODUCTS 
    : MERCH_PRODUCTS.filter(p => p.category === activeCategory);

  return (
    <div className="site" data-testid="shop-page">
      <Header />
      
      <section className="shop-hero">
        <div className="shop-hero-bg">
          <img src={MERCH_IMAGES.trackJacket} alt="ACE OF MIAMI Shop" />
          <div className="shop-hero-overlay"></div>
        </div>
        <div className="shop-hero-content">
          <span className="shop-tag">MEMBERS ONLY</span>
          <h1>ACE MERCH</h1>
          <p>Exclusive luxury apparel collection</p>
        </div>
      </section>

      <section className="shop-content">
        <div className="category-filter">
          {MERCH_CATEGORIES.map(cat => (
            <button 
              key={cat.id}
              className={`cat-btn ${activeCategory === cat.id ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`category-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="merch-product-grid">
          {filteredProducts.map(product => (
            <MerchProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* Membership CTA */}
      <section className="shop-membership-cta">
        <div className="shop-cta-content">
          <h2>MEMBERS ONLY ACCESS</h2>
          <p>Our merchandise is exclusively available to approved ACE OF MIAMI members. Apply for membership to unlock purchasing.</p>
          <Link to="/membership" className="btn-yellow">APPLY FOR MEMBERSHIP</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Product Detail Page
const MerchProductPage = () => {
  const { id } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const product = MERCH_PRODUCTS.find(p => p.id === id);

  if (!product) {
    return (
      <div className="site">
        <Header />
        <div className="not-found-section">
          <h1>Product Not Found</h1>
          <Link to="/shop" className="btn-yellow">Back to Shop</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = async () => {
    if (!auth?.user) {
      navigate("/login");
      return;
    }
    
    if (!selectedSize) {
      setMessage({ type: "error", text: "Please select a size" });
      return;
    }

    if (auth.user.membership_status !== "approved") {
      setMessage({ type: "error", text: "Only approved members can purchase. Apply for membership first." });
      return;
    }

    setLoading(true);
    try {
      await auth.addToCart(product.id, selectedSize, 1);
      setMessage({ type: "success", text: "Added to cart!" });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to add to cart" });
    }
    setLoading(false);
  };

  const canPurchase = auth?.user && auth.user.membership_status === "approved";

  return (
    <div className="site" data-testid="product-detail-page">
      <Header />
      
      <div className="merch-product-detail">
        <div className="merch-detail-image">
          <img src={product.image} alt={product.name} />
        </div>
        
        <div className="merch-detail-info">
          <span className="merch-category-tag">{product.category}</span>
          <h1>{product.name}</h1>
          <p className="merch-detail-price">${product.price}</p>
          
          <p className="merch-detail-desc">{product.description}</p>
          
          <div className="merch-fabric-info">
            <h4>FABRIC & CARE</h4>
            <p>{product.fabric}</p>
          </div>

          <div className="merch-size-selector">
            <h4>SIZE</h4>
            <div className="merch-sizes">
              {product.sizes.map(size => (
                <button 
                  key={size}
                  className={`merch-size-btn ${selectedSize === size ? "active" : ""}`}
                  onClick={() => setSelectedSize(size)}
                  data-testid={`size-${size}`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {message && (
            <div className={`merch-message ${message.type}`}>
              {message.type === "success" ? <Check size={16} /> : <AlertCircle size={16} />}
              {message.text}
            </div>
          )}

          {!auth?.user ? (
            <div className="merch-membership-required">
              <Lock size={20} />
              <div>
                <p><strong>Members Only</strong></p>
                <p>Please <Link to="/login">login</Link> or <Link to="/membership">apply for membership</Link> to purchase.</p>
              </div>
            </div>
          ) : !canPurchase ? (
            <div className="merch-membership-required">
              <Lock size={20} />
              <div>
                <p><strong>Membership Required</strong></p>
                <p>Your membership is {auth.user.membership_status === "pending" ? "pending approval" : "not approved"}. <Link to="/membership">Learn more</Link></p>
              </div>
            </div>
          ) : null}

          <button 
            className="merch-add-cart-btn"
            onClick={handleAddToCart}
            disabled={loading || !canPurchase}
            data-testid="add-to-cart-btn"
          >
            {loading ? "ADDING..." : canPurchase ? "ADD TO CART" : "MEMBERS ONLY"}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Cart Page
const CartPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  const subtotal = auth?.cart?.reduce((sum, item) => {
    const product = MERCH_PRODUCTS.find(p => p.id === item.product_id);
    return sum + (product ? product.price * item.quantity : 0);
  }, 0) || 0;

  const shipping = subtotal > 0 ? 15 : 0;
  const tax = Math.round(subtotal * 0.07 * 100) / 100;
  const total = subtotal + shipping + tax;

  const handleQuantityChange = async (item, delta) => {
    const newQty = item.quantity + delta;
    if (newQty < 1) {
      await auth.removeFromCart(item.id);
    } else {
      await auth.updateCartItem(item.id, newQty, item.size);
    }
  };

  if (!auth?.user) {
    return (
      <div className="site" data-testid="cart-page">
        <Header />
        <div className="cart-empty-section">
          <ShoppingBag size={64} />
          <h2>Please Login</h2>
          <p>You need to login to view your cart.</p>
          <button className="btn-yellow" onClick={() => navigate("/login")}>LOGIN</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!auth.cart || auth.cart.length === 0) {
    return (
      <div className="site" data-testid="cart-page">
        <Header />
        <div className="cart-empty-section">
          <ShoppingBag size={64} />
          <h2>Your Cart is Empty</h2>
          <p>Looks like you haven't added anything yet.</p>
          <button className="btn-yellow" onClick={() => navigate("/shop")}>CONTINUE SHOPPING</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="site" data-testid="cart-page">
      <Header />
      
      <div className="cart-section">
        <div className="cart-items-container">
          <h1>YOUR CART</h1>
          {auth.cart.map(item => {
            const product = MERCH_PRODUCTS.find(p => p.id === item.product_id);
            if (!product) return null;
            return (
              <div key={item.id} className="cart-item" data-testid={`cart-item-${item.id}`}>
                <img src={product.image} alt={product.name} />
                <div className="cart-item-info">
                  <h3>{product.name}</h3>
                  <p className="cart-item-size">Size: {item.size}</p>
                  <p className="cart-item-price">${product.price}</p>
                </div>
                <div className="cart-item-qty">
                  <button onClick={() => handleQuantityChange(item, -1)}><Minus size={16} /></button>
                  <span>{item.quantity}</span>
                  <button onClick={() => handleQuantityChange(item, 1)}><Plus size={16} /></button>
                </div>
                <button className="cart-item-remove" onClick={() => auth.removeFromCart(item.id)}>
                  <Trash2 size={20} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="cart-summary">
          <h2>ORDER SUMMARY</h2>
          <div className="summary-row">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Shipping</span>
            <span>${shipping.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Tax (7%)</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          <div className="summary-row total">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
          <button 
            className="btn-yellow checkout-btn"
            onClick={() => navigate("/shop/checkout")}
            data-testid="checkout-btn"
          >
            PROCEED TO CHECKOUT
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Shop Checkout Page
const ShopCheckoutPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [shipping, setShipping] = useState({
    full_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "United States"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const subtotal = auth?.cart?.reduce((sum, item) => {
    const product = MERCH_PRODUCTS.find(p => p.id === item.product_id);
    return sum + (product ? product.price * item.quantity : 0);
  }, 0) || 0;
  const shippingCost = 15;
  const tax = Math.round(subtotal * 0.07 * 100) / 100;
  const total = subtotal + shippingCost + tax;

  useEffect(() => {
    if (!auth?.user || auth.user.membership_status !== "approved") {
      navigate("/membership");
    }
    if (!auth?.cart || auth.cart.length === 0) {
      navigate("/cart");
    }
  }, [auth, navigate]);

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("aom_token");
      const { data } = await axios.post(`${API}/orders/create`, {
        shipping_address: shipping,
        billing_same_as_shipping: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOrderId(data.order_id);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create order");
    }
    setLoading(false);
  };

  if (!auth?.user || !auth?.cart || auth.cart.length === 0) return null;

  return (
    <div className="site" data-testid="shop-checkout-page">
      <Header />
      
      <div className="shop-checkout-section">
        <div className="checkout-steps">
          <div className={`checkout-step ${step >= 1 ? "active" : ""}`}>1. SHIPPING</div>
          <div className={`checkout-step ${step >= 2 ? "active" : ""}`}>2. PAYMENT</div>
        </div>

        <div className="checkout-main">
          {step === 1 && (
            <form className="shipping-form" onSubmit={handleShippingSubmit}>
              <h2>SHIPPING ADDRESS</h2>
              
              {error && <div className="checkout-error">{error}</div>}

              <div className="form-group">
                <label>Full Name</label>
                <input 
                  type="text" 
                  value={shipping.full_name}
                  onChange={e => setShipping({...shipping, full_name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input 
                  type="text" 
                  value={shipping.address_line1}
                  onChange={e => setShipping({...shipping, address_line1: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Apartment, suite, etc. (optional)</label>
                <input 
                  type="text" 
                  value={shipping.address_line2}
                  onChange={e => setShipping({...shipping, address_line2: e.target.value})}
                />
              </div>

              <div className="form-row-3">
                <div className="form-group">
                  <label>City</label>
                  <input 
                    type="text" 
                    value={shipping.city}
                    onChange={e => setShipping({...shipping, city: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input 
                    type="text" 
                    value={shipping.state}
                    onChange={e => setShipping({...shipping, state: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input 
                    type="text" 
                    value={shipping.zip_code}
                    onChange={e => setShipping({...shipping, zip_code: e.target.value})}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn-yellow" disabled={loading}>
                {loading ? "PROCESSING..." : "CONTINUE TO PAYMENT"}
              </button>
            </form>
          )}

          {step === 2 && orderId && (
            <ShopPaymentForm orderId={orderId} total={total} refreshCart={auth.refreshCart} />
          )}
        </div>

        <div className="checkout-sidebar">
          <h3>ORDER SUMMARY</h3>
          {auth.cart.map(item => {
            const product = MERCH_PRODUCTS.find(p => p.id === item.product_id);
            if (!product) return null;
            return (
              <div key={item.id} className="checkout-summary-item">
                <img src={product.image} alt={product.name} />
                <div>
                  <p>{product.name}</p>
                  <p className="small">Size: {item.size} × {item.quantity}</p>
                </div>
                <span>${(product.price * item.quantity).toFixed(2)}</span>
              </div>
            );
          })}
          <div className="checkout-totals">
            <div className="checkout-total-row"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="checkout-total-row"><span>Shipping</span><span>${shippingCost.toFixed(2)}</span></div>
            <div className="checkout-total-row"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
            <div className="checkout-total-row final"><span>Total</span><span>${total.toFixed(2)}</span></div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Shop Payment Form (Square)
const ShopPaymentForm = ({ orderId, total, refreshCart }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [squareCard, setSquareCard] = useState(null);
  const [squarePayments, setSquarePayments] = useState(null);
  const [squareReady, setSquareReady] = useState(false);
  const cardContainerRef = React.useRef(null);

  useEffect(() => {
    let card = null;

    const loadSdk = () => new Promise((resolve, reject) => {
      if (window.Square) return resolve();
      const script = document.createElement('script');
      script.src = 'https://web.squarecdn.com/v1/square.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Square SDK'));
      document.body.appendChild(script);
    });

    const initSquare = async () => {
      try {
        await loadSdk();
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        setSquarePayments(payments);
        card = await payments.card({
          style: {
            '.input-container': {
              borderColor: '#e2e8f0',
              borderRadius: '6px',
            },
            '.input-container.is-focus': {
              borderColor: '#fbbf24',
            },
            'input': {
              fontSize: '15px',
              color: '#0d1b2e',
            },
            'input::placeholder': {
              color: '#94a3b8',
            },
          }
        });
        if (cardContainerRef.current) {
          await card.attach(cardContainerRef.current);
          setSquareCard(card);
          setSquareReady(true);
        }
      } catch (err) {
        console.error('Square init error:', err);
        setError('Failed to initialize payment form');
      }
    };

    initSquare();

    return () => {
      if (card) {
        try { card.destroy(); } catch (e) {}
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!squareCard) return;

    setLoading(true);
    setError(null);

    try {
      const result = await squareCard.tokenize();
      if (result.status !== 'OK') {
        const errorMsg = result.errors?.[0]?.message || 'Card processing failed';
        setError(errorMsg);
        setLoading(false);
        return;
      }

      // 3DS / Buyer Verification (required for most international cards)
      let verificationToken;
      if (squarePayments) {
        try {
          const names = (cardholderName || '').trim().split(/\s+/);
          const verifyResult = await squarePayments.verifyBuyer(result.token, {
            amount: parseFloat(total).toFixed(2),
            billingContact: {
              givenName: names[0] || '',
              familyName: names.slice(1).join(' ') || '',
            },
            currencyCode: 'USD',
            intent: 'CHARGE',
          });
          verificationToken = verifyResult?.token;
        } catch (err) {
          console.warn('verifyBuyer failed (continuing without 3DS):', err?.message || err);
        }
      }

      // Charge via Square
      const { data: payData } = await axios.post(`${API}/square/create-payment`, {
        source_id: result.token,
        verification_token: verificationToken,
        amount: parseFloat(total),
        currency: 'USD',
        order_id: orderId,
        location_id: SQUARE_LOCATION_ID
      });

      if (!payData.success) {
        throw new Error(payData.detail || 'Payment failed');
      }

      // Mark order complete
      const token = localStorage.getItem("aom_token");
      await axios.post(`${API}/orders/${orderId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      refreshCart();
      navigate("/shop/order-success");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Payment failed');
    }
    setLoading(false);
  };

  return (
    <form className="shop-payment-form" onSubmit={handleSubmit} data-testid="shop-payment-form">
      <h2>PAYMENT</h2>
      {error && <div className="checkout-error" data-testid="shop-payment-error">{error}</div>}

      <div className="form-group">
        <label>Cardholder Name</label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          required
          data-testid="shop-cardholder-name-input"
        />
      </div>

      <div className="form-group">
        <label>Card Details</label>
        <div
          ref={cardContainerRef}
          style={{
            minHeight: '48px',
            border: '1.5px solid #e2e8f0',
            borderRadius: '6px',
            padding: '12px'
          }}
        ></div>
      </div>

      <button
        type="submit"
        className="btn-yellow"
        disabled={!squareReady || loading}
        data-testid="shop-pay-btn"
      >
        {loading ? "PROCESSING..." : `PAY $${total.toFixed(2)}`}
      </button>

      <p className="secure-note"><Lock size={14} /> Secure payment powered by Square</p>
    </form>
  );
};

// Order Success Page
const ShopOrderSuccessPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="site" data-testid="order-success-page">
      <Header />
      <div className="order-success-section">
        <div className="success-icon"><Check size={64} /></div>
        <h1>ORDER CONFIRMED</h1>
        <p>Thank you for your purchase. You will receive an email confirmation shortly.</p>
        <button className="btn-yellow" onClick={() => navigate("/shop")}>CONTINUE SHOPPING</button>
      </div>
      <Footer />
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", full_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (mode === "login") {
        await auth.login(form.email, form.password);
      } else {
        await auth.register(form.email, form.password, form.full_name);
      }
      navigate("/shop");
    } catch (e) {
      setError(e.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  };

  return (
    <div className="site" data-testid="login-page">
      <Header />
      
      <div className="auth-section">
        <div className="auth-card">
          <h1>{mode === "login" ? "LOGIN" : "CREATE ACCOUNT"}</h1>
          
          {error && <div className="auth-error">{error}</div>}
          
          <form onSubmit={handleSubmit}>
            {mode === "register" && (
              <div className="form-group">
                <label>Full Name</label>
                <input 
                  type="text"
                  value={form.full_name}
                  onChange={e => setForm({...form, full_name: e.target.value})}
                  required
                />
              </div>
            )}
            
            <div className="form-group">
              <label>Email</label>
              <input 
                type="email"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <input 
                type="password"
                value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                required
              />
            </div>
            
            <button type="submit" className="btn-yellow" disabled={loading}>
              {loading ? "LOADING..." : mode === "login" ? "LOGIN" : "CREATE ACCOUNT"}
            </button>
          </form>
          
          <div className="auth-switch">
            {mode === "login" ? (
              <p>Don't have an account? <button onClick={() => setMode("register")}>Create one</button></p>
            ) : (
              <p>Already have an account? <button onClick={() => setMode("login")}>Login</button></p>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Membership Page
const MembershipPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    instagram: "",
    reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (auth?.user) {
      setForm(f => ({ ...f, full_name: auth.user.full_name || "", email: auth.user.email || "" }));
    }
  }, [auth?.user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth?.user) {
      navigate("/login");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("aom_token");
      await axios.post(`${API}/membership/apply`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(true);
      // Refresh user data
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      auth.setUser(data.user);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit application");
    }
    setLoading(false);
  };

  const renderStatus = () => {
    if (!auth?.user) return null;
    
    switch (auth.user.membership_status) {
      case "approved":
        return (
          <div className="membership-status approved">
            <Check size={32} />
            <h3>MEMBERSHIP APPROVED</h3>
            <p>Welcome to ACE OF MIAMI. You have full access to our exclusive merchandise collection.</p>
            <button className="btn-yellow" onClick={() => navigate("/shop")}>SHOP NOW</button>
          </div>
        );
      case "pending":
        return (
          <div className="membership-status pending">
            <AlertCircle size={32} />
            <h3>APPLICATION PENDING</h3>
            <p>Your membership application is being reviewed. We'll notify you once a decision is made.</p>
          </div>
        );
      case "rejected":
        return (
          <div className="membership-status rejected">
            <X size={32} />
            <h3>APPLICATION DECLINED</h3>
            <p>Unfortunately, your membership application was not approved at this time.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="site" data-testid="membership-page">
      <Header />
      
      <section className="membership-hero">
        <div className="membership-hero-bg">
          <img src={MERCH_IMAGES.miamiGoldHoodie} alt="Membership" />
          <div className="membership-hero-overlay"></div>
        </div>
        <div className="membership-hero-content">
          <h1>MEMBERSHIP</h1>
          <p>ACE OF MIAMI merchandise is exclusively available to approved members.</p>
        </div>
      </section>

      <section className="membership-content">
        <div className="membership-info">
          <h2>WHY MEMBERSHIP?</h2>
          <p>We believe in exclusivity. Our limited production runs mean that not everyone can own ACE OF MIAMI merchandise. Membership ensures our community remains tight-knit and our products remain scarce.</p>
          
          <div className="membership-benefits">
            <div className="benefit">
              <span className="benefit-icon">✦</span>
              <h4>EARLY ACCESS</h4>
              <p>Be first to shop new drops before public release.</p>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✦</span>
              <h4>EXCLUSIVE PIECES</h4>
              <p>Members-only items never available to the public.</p>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✦</span>
              <h4>PRIORITY SUPPORT</h4>
              <p>Direct line to our team for any needs.</p>
            </div>
          </div>
        </div>

        <div className="membership-application">
          {auth?.user && auth.user.membership_status !== "none" ? (
            renderStatus()
          ) : success ? (
            <div className="membership-status success">
              <Check size={32} />
              <h3>APPLICATION SUBMITTED</h3>
              <p>Thank you for applying. We'll review your application and get back to you soon.</p>
            </div>
          ) : (
            <>
              <h2>APPLY FOR MEMBERSHIP</h2>
              {!auth?.user && (
                <div className="login-prompt">
                  <p>Please <Link to="/login">login or create an account</Link> first to apply for membership.</p>
                </div>
              )}
              
              {error && <div className="auth-error">{error}</div>}
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input 
                    type="text"
                    value={form.full_name}
                    onChange={e => setForm({...form, full_name: e.target.value})}
                    required
                    disabled={!auth?.user}
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email"
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    required
                    disabled={!auth?.user}
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone</label>
                  <input 
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    required
                    disabled={!auth?.user}
                  />
                </div>
                
                <div className="form-group">
                  <label>Instagram Handle (optional)</label>
                  <input 
                    type="text"
                    value={form.instagram}
                    onChange={e => setForm({...form, instagram: e.target.value})}
                    placeholder="@username"
                    disabled={!auth?.user}
                  />
                </div>
                
                <div className="form-group">
                  <label>Why do you want to join ACE OF MIAMI?</label>
                  <textarea 
                    value={form.reason}
                    onChange={e => setForm({...form, reason: e.target.value})}
                    required
                    rows={4}
                    disabled={!auth?.user}
                  />
                </div>
                
                <button type="submit" className="btn-yellow" disabled={loading || !auth?.user}>
                  {loading ? "SUBMITTING..." : "SUBMIT APPLICATION"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Account Page
const AccountPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!auth?.user) {
      navigate("/login");
      return;
    }
    fetchOrders();
  }, [auth?.user, navigate]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("aom_token");
      const { data } = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(data.orders || []);
    } catch (e) {
      console.error("Error fetching orders:", e);
    }
  };

  if (!auth?.user) return null;

  return (
    <div className="site" data-testid="account-page">
      <Header />
      
      <div className="account-section">
        <h1>MY ACCOUNT</h1>
        
        <div className="account-grid">
          <div className="account-info">
            <h2>PROFILE</h2>
            <p><strong>Name:</strong> {auth.user.full_name}</p>
            <p><strong>Email:</strong> {auth.user.email}</p>
            <p><strong>Membership:</strong> <span className={`status-badge ${auth.user.membership_status}`}>{auth.user.membership_status}</span></p>
            
            {auth.user.is_admin && (
              <button className="btn-outline-dark" onClick={() => navigate("/admin")}>
                ADMIN DASHBOARD
              </button>
            )}
            
            <button className="btn-logout" onClick={() => { auth.logout(); navigate("/"); }}>
              LOGOUT
            </button>
          </div>
          
          <div className="account-orders">
            <h2>ORDER HISTORY</h2>
            {orders.length === 0 ? (
              <p className="no-orders">No orders yet.</p>
            ) : (
              <div className="orders-list">
                {orders.map(order => (
                  <div key={order.id} className="order-card">
                    <div className="order-header">
                      <span>Order #{order.id.slice(0, 8)}</span>
                      <span className={`order-status ${order.status}`}>{order.status}</span>
                    </div>
                    <div className="order-items">
                      {order.items.map((item, i) => (
                        <p key={i}>{item.name} ({item.size}) × {item.quantity}</p>
                      ))}
                    </div>
                    <div className="order-total">
                      <span>Total:</span>
                      <span>${order.total.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [allOrders, setAllOrders] = useState([]);

  useEffect(() => {
    if (!auth?.user || !auth.user.is_admin) {
      navigate("/");
      return;
    }
    fetchData();
  }, [auth?.user, navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem("aom_token");
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [appsRes, usersRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/orders`, { headers })
      ]);
      setApplications(appsRes.data.applications || []);
      setUsers(usersRes.data.users || []);
      setAllOrders(ordersRes.data.orders || []);
    } catch (e) {
      console.error("Error fetching admin data:", e);
    }
  };

  const handleApprove = async (appId) => {
    const token = localStorage.getItem("aom_token");
    await axios.post(`${API}/admin/applications/${appId}/approve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchData();
  };

  const handleReject = async (appId) => {
    const token = localStorage.getItem("aom_token");
    await axios.post(`${API}/admin/applications/${appId}/reject`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchData();
  };

  if (!auth?.user || !auth.user.is_admin) return null;

  return (
    <div className="site" data-testid="admin-page">
      <Header />
      
      <div className="admin-section">
        <h1>ADMIN DASHBOARD</h1>
        
        <div className="admin-tabs">
          <button className={tab === "applications" ? "active" : ""} onClick={() => setTab("applications")}>
            Applications ({applications.filter(a => a.status === "pending").length})
          </button>
          <button className={tab === "users" ? "active" : ""} onClick={() => setTab("users")}>
            Users ({users.length})
          </button>
          <button className={tab === "orders" ? "active" : ""} onClick={() => setTab("orders")}>
            Orders ({allOrders.length})
          </button>
        </div>

        {tab === "applications" && (
          <div className="admin-content">
            <h2>MEMBERSHIP APPLICATIONS</h2>
            {applications.length === 0 ? (
              <p>No applications yet.</p>
            ) : (
              <div className="admin-table">
                {applications.map(app => (
                  <div key={app.id} className="admin-row">
                    <div className="app-info">
                      <h4>{app.full_name}</h4>
                      <p>{app.email} • {app.phone}</p>
                      {app.instagram && <p>Instagram: {app.instagram}</p>}
                      <p className="app-reason">"{app.reason}"</p>
                    </div>
                    <div className="app-status">
                      <span className={`status-badge ${app.status}`}>{app.status}</span>
                    </div>
                    {app.status === "pending" && (
                      <div className="app-actions">
                        <button className="btn-approve" onClick={() => handleApprove(app.id)}>APPROVE</button>
                        <button className="btn-reject" onClick={() => handleReject(app.id)}>REJECT</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "users" && (
          <div className="admin-content">
            <h2>ALL USERS</h2>
            <div className="admin-table">
              {users.map(u => (
                <div key={u.id} className="admin-row">
                  <div className="user-info">
                    <h4>{u.full_name}</h4>
                    <p>{u.email}</p>
                  </div>
                  <span className={`status-badge ${u.membership_status}`}>{u.membership_status}</span>
                  {u.is_admin && <span className="admin-badge">ADMIN</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="admin-content">
            <h2>ALL ORDERS</h2>
            {allOrders.length === 0 ? (
              <p>No orders yet.</p>
            ) : (
              <div className="admin-table">
                {allOrders.map(order => (
                  <div key={order.id} className="admin-row order-row">
                    <div className="order-info">
                      <h4>Order #{order.id.slice(0, 8)}</h4>
                      <p>{order.items.length} items • ${order.total.toFixed(2)}</p>
                    </div>
                    <span className={`status-badge ${order.status}`}>{order.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

// Shop Policy Pages
const ShopShippingPage = () => (
  <div className="site" data-testid="shop-shipping-page">
    <Header />
    <section className="legal-hero"><h1>SHIPPING POLICY</h1><p>Last Updated: December 2025</p></section>
    <section className="legal-content">
      <div className="legal-container">
        <h2>Processing Time</h2>
        <p>Orders are processed within 2-3 business days. You will receive a confirmation email with tracking information once your order ships.</p>
        <h2>Domestic Shipping (USA)</h2>
        <p>Standard shipping: 5-7 business days ($15 flat rate)</p>
        <h2>International Shipping</h2>
        <p>We currently ship to select international destinations. International orders may be subject to customs fees and import duties.</p>
        <h2>Contact</h2>
        <p>Questions? Contact us at info@aceofmiami.com</p>
      </div>
    </section>
    <Footer />
  </div>
);

const ShopRefundsPage = () => (
  <div className="site" data-testid="shop-refunds-page">
    <Header />
    <section className="legal-hero"><h1>REFUND POLICY</h1><p>Last Updated: December 2025</p></section>
    <section className="legal-content">
      <div className="legal-container">
        <h2>Returns</h2>
        <p>We accept returns within 14 days of delivery for unworn, unwashed items in original condition with tags attached.</p>
        <h2>Return Process</h2>
        <p>To initiate a return, email info@aceofmiami.com with your order number and reason for return.</p>
        <h2>Refunds</h2>
        <p>Once we receive and inspect your return, we will process your refund within 5-7 business days to the original payment method.</p>
        <h2>Non-Returnable Items</h2>
        <p>Items marked as final sale are not eligible for return or exchange.</p>
      </div>
    </section>
    <Footer />
  </div>
);

// ============== SQUARE-ONLY CHECKOUT (for bioinfinitylab.com) ==============

const SquareCheckout = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [squareCard, setSquareCard] = useState(null);
  const [squareApplePay, setSquareApplePay] = useState(null);
  const [squarePayments, setSquarePayments] = useState(null);
  const [squareReady, setSquareReady] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const cardContainerRef = React.useRef(null);
  const applePayContainerRef = React.useRef(null);
  
  const amount = searchParams.get("amount");
  const currency = searchParams.get("currency") || "USD";
  const orderId = searchParams.get("orderId");
  const email = searchParams.get("email") || "";

  // Load Square SDK
  useEffect(() => {
    if (window.Square) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => setSdkLoaded(true);
    script.onerror = () => setError('Failed to load payment system');
    document.body.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize Square Payments
  useEffect(() => {
    if (!sdkLoaded || !amount || !orderId) return;

    let card = null;
    let applePay = null;

    const initSquare = async () => {
      try {
        // Initialize Square Payments - only pass App ID, location ID is used in payment request
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        setSquarePayments(payments);
        
        // Initialize Card
        card = await payments.card({
          style: {
            '.input-container': {
              borderColor: '#e2e8f0',
              borderRadius: '8px',
            },
            '.input-container.is-focus': {
              borderColor: '#00C896',
            },
            '.message-text': {
              color: '#0d1b2e',
            },
            '.message-icon': {
              color: '#00C896',
            },
            '.message-text.is-error': {
              color: '#e53e3e',
            },
            'input': {
              fontSize: '15px',
              color: '#0d1b2e',
            },
            'input::placeholder': {
              color: '#94a3b8',
            },
            'input.is-error': {
              color: '#e53e3e',
            },
          }
        });
        
        if (cardContainerRef.current) {
          await card.attach(cardContainerRef.current);
          setSquareCard(card);
        }

        // Try Apple Pay
        try {
          const paymentRequest = payments.paymentRequest({
            countryCode: 'US',
            currencyCode: currency.toUpperCase(),
            total: {
              amount: parseFloat(amount).toFixed(2),
              label: 'BIO-MIAMI',
            },
          });
          
          applePay = await payments.applePay(paymentRequest);
          if (applePayContainerRef.current) {
            await applePay.attach(applePayContainerRef.current);
            setSquareApplePay(applePay);
          }
        } catch (applePayError) {
          console.log('Apple Pay not available:', applePayError.message);
        }

        setSquareReady(true);
      } catch (err) {
        console.error('Square init error:', err);
        setError('Failed to initialize payment form: ' + err.message);
      }
    };

    initSquare();

    return () => {
      if (card) {
        try { card.destroy(); } catch (e) {}
      }
      if (applePay) {
        try { applePay.destroy(); } catch (e) {}
      }
    };
  }, [sdkLoaded, amount, currency, orderId]);

  const handleClose = () => {
    sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'cancelled', orderId });
  };

  const verifyBuyer = async (token) => {
    if (!squarePayments) return undefined;
    try {
      const names = (cardholderName || '').trim().split(/\s+/);
      const verificationDetails = {
        amount: parseFloat(amount).toFixed(2),
        billingContact: {
          givenName: names[0] || '',
          familyName: names.slice(1).join(' ') || '',
        },
        currencyCode: currency.toUpperCase(),
        intent: 'CHARGE',
      };
      const verificationResults = await squarePayments.verifyBuyer(token, verificationDetails);
      return verificationResults?.token;
    } catch (err) {
      console.warn('verifyBuyer failed (continuing without 3DS):', err?.message || err);
      return undefined;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!squareCard) return;
    setLoading(true);
    setError(null);

    try {
      const result = await squareCard.tokenize();
      
      if (result.status === 'OK') {
        const verificationToken = await verifyBuyer(result.token);
        const { data } = await axios.post(`${API}/square/create-payment`, {
          source_id: result.token,
          verification_token: verificationToken,
          amount: parseFloat(amount),
          currency,
          order_id: orderId,
          location_id: SQUARE_LOCATION_ID
        });

        if (data.success) {
          setSuccess(true);
          sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'success', sessionId: data.paymentId, orderId });
        } else {
          throw new Error(data.detail || 'Payment failed');
        }
      } else {
        const errorMsg = result.errors?.[0]?.message || 'Card processing failed';
        setError(errorMsg);
        sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'failed', error: errorMsg });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Payment failed';
      setError(errorMsg);
      sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'failed', error: errorMsg });
    }
    setLoading(false);
  };

  const handleApplePay = async () => {
    if (!squareApplePay) return;
    setLoading(true);
    setError(null);

    try {
      const result = await squareApplePay.tokenize();
      
      if (result.status === 'OK') {
        const verificationToken = await verifyBuyer(result.token);
        const { data } = await axios.post(`${API}/square/create-payment`, {
          source_id: result.token,
          verification_token: verificationToken,
          amount: parseFloat(amount),
          currency,
          order_id: orderId,
          location_id: SQUARE_LOCATION_ID
        });

        if (data.success) {
          setSuccess(true);
          sendPostMessage({ type: 'STRIPE_PAYMENT', status: 'success', sessionId: data.paymentId, orderId });
        } else {
          throw new Error(data.detail || 'Payment failed');
        }
      } else {
        const errorMsg = result.errors?.[0]?.message || 'Apple Pay failed';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Apple Pay failed';
      setError(errorMsg);
    }
    setLoading(false);
  };

  // Missing parameters
  if (!amount || !orderId) {
    return (
      <div style={squareStyles.modal}>
        <div style={squareStyles.card}>
          <div style={squareStyles.errorPage}>
            <div style={squareStyles.errorIcon}>✕</div>
            <h2 style={squareStyles.errorTitle}>Missing Parameters</h2>
            <p style={squareStyles.errorText}>Amount and Order ID are required.</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={squareStyles.modal}>
        <div style={squareStyles.card}>
          <div style={squareStyles.successContainer}>
            <div style={squareStyles.successIcon}>✓</div>
            <h2 style={squareStyles.successTitle}>Payment Successful!</h2>
            <p style={squareStyles.successText}>Order: {orderId}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={squareStyles.modal}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @keyframes sqFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sqSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes sqSpin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>
      <div style={squareStyles.card}>
        {/* Header */}
        <div style={squareStyles.header}>
          <h1 style={squareStyles.headerTitle}>BIO-MIAMI</h1>
          <button type="button" onClick={handleClose} style={squareStyles.closeBtn}>✕</button>
        </div>

        {/* Amount */}
        <div style={squareStyles.amountSection}>
          <span style={squareStyles.amount}>${parseFloat(amount).toFixed(2)} {currency}</span>
        </div>

        {/* Divider */}
        <div style={squareStyles.divider}></div>

        {/* Apple Pay */}
        {squareApplePay && (
          <div style={{ marginBottom: '24px' }}>
            <div 
              ref={applePayContainerRef} 
              onClick={handleApplePay}
              style={{ minHeight: '48px', cursor: 'pointer' }}
            ></div>
            <div style={squareStyles.orDivider}>
              <div style={squareStyles.orLine}></div>
              <span style={squareStyles.orText}>or pay with card</span>
              <div style={squareStyles.orLine}></div>
            </div>
          </div>
        )}

        {/* Card Form */}
        <form onSubmit={handleSubmit}>
          {/* Cardholder Name */}
          <div style={squareStyles.fieldGroup}>
            <label style={squareStyles.label}>Cardholder Name</label>
            <input
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="Name on card"
              required
              style={squareStyles.input}
            />
          </div>

          {/* Square Card Element */}
          <div style={squareStyles.fieldGroup}>
            <label style={squareStyles.label}>Card Details</label>
            <div 
              ref={cardContainerRef} 
              id="card-container"
              style={squareStyles.cardContainer}
            >
              {!squareReady && !error && (
                <div style={squareStyles.loadingCard}>
                  <div style={squareStyles.spinnerSmall}></div>
                  <span>Loading...</span>
                </div>
              )}
            </div>
          </div>

          {/* Trust Badges */}
          <div style={squareStyles.trustBadges}>
            <span style={squareStyles.badge}>🔒 256-bit SSL</span>
            <span style={squareStyles.badge}>✓ PCI DSS</span>
            <span style={squareStyles.badge}>✓ Secure</span>
          </div>

          {/* Error */}
          {error && (
            <div style={squareStyles.errorBox}>{error}</div>
          )}

          {/* Pay Button */}
          <button 
            type="submit" 
            disabled={!squareReady || loading} 
            style={{
              ...squareStyles.payBtn,
              ...(loading || !squareReady ? squareStyles.payBtnDisabled : {})
            }}
          >
            {loading ? (
              <span style={squareStyles.spinner}></span>
            ) : (
              `Pay $${parseFloat(amount).toFixed(2)} →`
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Square Checkout Styles
const squareStyles = {
  modal: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(13, 27, 46, 0.5)',
    padding: '16px',
    animation: 'sqFadeIn 280ms ease-out',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(13, 27, 46, 0.2)',
    animation: 'sqSlideUp 280ms ease-out',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#0d1b2e',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#0d1b2e',
    cursor: 'pointer',
    padding: '8px',
    lineHeight: 1,
    opacity: 0.6,
  },
  amountSection: {
    marginBottom: '16px',
  },
  amount: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#0d1b2e',
    letterSpacing: '-1px',
  },
  divider: {
    height: '1px',
    backgroundColor: '#e2e8f0',
    marginBottom: '24px',
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
  },
  orLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e2e8f0',
  },
  orText: {
    padding: '0 16px',
    fontSize: '13px',
    color: '#94a3b8',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#0d1b2e',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#0d1b2e',
    backgroundColor: '#ffffff',
    border: '1.5px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '48px',
  },
  cardContainer: {
    minHeight: '100px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px',
    backgroundColor: '#fff',
  },
  loadingCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: '#94a3b8',
    fontSize: '14px',
    padding: '20px',
  },
  spinnerSmall: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,200,150,0.2)',
    borderTopColor: '#00C896',
    borderRadius: '50%',
    animation: 'sqSpin 600ms linear infinite',
  },
  trustBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px',
    justifyContent: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#0AAFA0',
    backgroundColor: '#e6faf6',
    borderRadius: '100px',
  },
  errorBox: {
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#e53e3e',
    backgroundColor: '#fff5f5',
    borderLeft: '3px solid #e53e3e',
    borderRadius: '0 8px 8px 0',
  },
  payBtn: {
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#ffffff',
    backgroundColor: '#00C896',
    border: 'none',
    borderRadius: '100px',
    cursor: 'pointer',
    minHeight: '52px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  payBtnDisabled: {
    backgroundColor: '#a0e8d8',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'sqSpin 600ms linear infinite',
  },
  successContainer: {
    padding: '48px 24px',
    textAlign: 'center',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 20px',
    backgroundColor: '#00C896',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: '#ffffff',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#0d1b2e',
    margin: '0 0 8px',
  },
  successText: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  errorPage: {
    padding: '48px 24px',
    textAlign: 'center',
  },
  errorIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 20px',
    backgroundColor: '#fee2e2',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: '#e53e3e',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0d1b2e',
    margin: '0 0 8px',
  },
  errorText: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/checkout/external" element={<SquareCheckout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          {/* Shop Routes */}
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:id" element={<MerchProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/shop/checkout" element={<ShopCheckoutPage />} />
          <Route path="/shop/order-success" element={<ShopOrderSuccessPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/shop/shipping" element={<ShopShippingPage />} />
          <Route path="/shop/refunds" element={<ShopRefundsPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
